import { useEffect, useState } from "react";
import type { PurchaseOrderLineDto } from "../../../../api";
import { Text, Heading } from "../../../../components/common/typography";
import { CurrencyAmount } from "../../../../components/common/currency_amount";
import { Image } from "../../../../components/common/image";
import { api, apiBase } from "../../../api_client";

function getLineImageUrl(line: PurchaseOrderLineDto): string | null {
  if (line.image) return line.image;
  if (line.product?.id == null) return null;
  if (!apiBase) return `/api/v1/product-images/variant/${line.product.id}`;
  return `${apiBase}/api/v1/product-images/variant/${line.product.id}`;
}

function AuthenticatedImage({
  src,
  alt,
}: {
  src: string;
  alt: string;
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    let objectUrl: string | null = null;

    setBlobUrl(null);

    api
      .get(src, { responseType: "blob" })
      .then((response) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(response.data);
        setBlobUrl(objectUrl);
      })
      .catch(() => {
        if (!active) return;
        setBlobUrl(null);
      });

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  return <Image image={blobUrl} alt={alt} size="w-full h-full object-cover rounded" />;
}

function LineThumbnail({ line }: { line: PurchaseOrderLineDto }) {
  return getLineImageUrl(line) ? (
    <div className="w-12 h-12 flex-shrink-0">
      <AuthenticatedImage
        src={getLineImageUrl(line)!}
        alt={line.name || line.product?.name || "Product"}
      />
    </div>
  ) : (
    <div className="w-12 h-12 flex-shrink-0 bg-uiSecondary rounded flex items-center justify-center">
      {line.product?.name ? (
        <Text variant="bodyXs" tone="body">
          {line.product.name.charAt(0).toUpperCase()}
        </Text>
      ) : (
        <Text variant="bodyXs" tone="body">
          —
        </Text>
      )}
    </div>
  );
}

interface PurchaseOrderProductsAndSummaryProps {
  orderLines: PurchaseOrderLineDto[];
  taxPercentage: number;
  amountUntaxed: number;
  amountTax: number;
  amountTotal: number;
  currency?: string;
}

export function PurchaseOrderProductsAndSummary({
  orderLines,
  taxPercentage,
  amountUntaxed,
  amountTax,
  amountTotal,
  currency = "QAR",
}: PurchaseOrderProductsAndSummaryProps) {
  return (
    <div className="bg-bgDefault rounded-xl p-6 border border-uiSecondary max-md:p-4">
      <Heading variant="h3" tone="dark" className="mb-4">
        Products
      </Heading>
      {/* Phones: one stacked row per line instead of the five-column table. */}
      <div className="mb-6 flex flex-col md:hidden">
        {orderLines.map((line) => (
          <div
            key={line.id}
            className="flex items-start gap-3 border-b border-uiSecondary py-3 last:border-b-0"
          >
            <LineThumbnail line={line} />
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <Text variant="body" tone="dark">
                {line.name || line.product?.name || "—"}
              </Text>
              <Text variant="bodySm" tone="muted">
                {line.quantity.toFixed(2)} Units ×{" "}
                <CurrencyAmount amount={line.priceUnit} currency={currency} variant="bodySm" tone="muted" />
              </Text>
            </div>
            <CurrencyAmount amount={line.priceTotal} currency={currency} variant="bodyBold" />
          </div>
        ))}
      </div>

      <div className="overflow-x-auto mb-6 max-md:hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-uiSecondary">
              <th className="text-left py-3 px-4">
                <Text variant="bodyLgBold" tone="dark">
                  Products
                </Text>
              </th>
              <th className="text-left py-3 px-4">
                <Text variant="bodyLgBold" tone="dark">
                  Quantity
                </Text>
              </th>
              <th className="text-right py-3 px-4">
                <Text variant="bodyLgBold" tone="dark">
                  Unit Price
                </Text>
              </th>
              <th className="text-right py-3 px-4">
                <Text variant="bodyLgBold" tone="dark">
                  Subtotal
                </Text>
              </th>
              <th className="text-right py-3 px-4">
                <Text variant="bodyLgBold" tone="dark">
                  Total
                </Text>
              </th>
            </tr>
          </thead>
          <tbody>
            {orderLines.map((line) => (
              <tr
                key={line.id}
                className="border-b border-uiSecondary last:border-b-0"
              >
                <td className="py-3 px-4">
                  <div className="flex items-center gap-3">
                    <LineThumbnail line={line} />
                    <Text variant="body" tone="dark">
                      {line.name || line.product?.name || "—"}
                    </Text>
                  </div>
                </td>
                <td className="py-3 px-4">
                  <Text variant="body" tone="body">
                    {line.quantity.toFixed(2)} Units
                  </Text>
                </td>
                <td className="py-3 px-4 text-right">
                  <CurrencyAmount amount={line.priceUnit} currency={currency} />
                </td>
                <td className="py-3 px-4 text-right">
                  <CurrencyAmount amount={line.priceSubtotal} currency={currency} />
                </td>
                <td className="py-3 px-4 text-right">
                  <CurrencyAmount amount={line.priceTotal} currency={currency} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Amount Summary */}
      <div className="flex flex-col gap-3 pt-6 border-t border-uiSecondary">
        <div className="flex justify-between items-center">
          <Text variant="body" tone="body">
            Untaxed Amount:
          </Text>
          <CurrencyAmount amount={amountUntaxed} currency={currency} />
        </div>
        <div className="flex justify-between items-center">
          <Text variant="body" tone="body">
            Tax {taxPercentage}%:
          </Text>
          <CurrencyAmount amount={amountTax} currency={currency} />
        </div>
        <div className="flex justify-between items-center pt-3 border-t border-uiSecondary">
          <Heading variant="h3" tone="dark">
            Total:
          </Heading>
          <CurrencyAmount amount={amountTotal} currency={currency} variant="h3" />
        </div>
      </div>
    </div>
  );
}
